import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { MessageCircle, Camera, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import type { Channel } from "@/types/channel";

const channelSchema = z.object({
  name: z.string().min(1, "Nome obrigatório"),
  identifier: z.string(),
  type: z.enum([
    "whatsapp_unofficial",
    "whatsapp_business",
    "instagram",
    "telegram",
  ]),
  provider: z.string().min(1, "Provider obrigatório"),
  status: z.string(),
});

type ChannelFormData = z.infer<typeof channelSchema>;

interface ChannelFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ChannelFormData) => Promise<void>;
  channel?: Channel | null;
}

const channelTypeOptions = [
  {
    value: "whatsapp_unofficial",
    label: "WhatsApp Não Oficial",
    icon: MessageCircle,
    color: "text-green-600",
    available: true,
  },
  {
    value: "whatsapp_business",
    label: "WhatsApp Business",
    icon: MessageCircle,
    color: "text-green-600",
    available: false,
  },
  {
    value: "instagram",
    label: "Instagram",
    icon: Camera,
    color: "text-pink-500",
    available: false,
  },
  {
    value: "telegram",
    label: "Telegram",
    icon: Send,
    color: "text-blue-500",
    available: false,
  },
] as const;

const providersByType: Record<string, { value: string; label: string }[]> = {
  whatsapp_unofficial: [{ value: "gwhatsmeow", label: "GWhatsmeow" }],
};

function getDefaultValues(channel?: Channel | null): ChannelFormData {
  if (channel) {
    return {
      name: channel.name,
      identifier: channel.identifier || "",
      type: channel.type,
      provider: channel.provider,
      status: channel.status,
    };
  }
  return {
    name: "",
    identifier: "",
    type: "whatsapp_unofficial",
    provider: "gwhatsmeow",
    status: "active",
  };
}

export function ChannelForm({
  open,
  onClose,
  onSubmit,
  channel,
}: ChannelFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ChannelFormData>({
    resolver: zodResolver(channelSchema),
    defaultValues: getDefaultValues(channel),
  });

  useEffect(() => {
    if (open) {
      reset(getDefaultValues(channel));
    }
  }, [open, channel, reset]);

  const currentType = watch("type");
  const currentStatus = watch("status");
  const currentProvider = watch("provider");

  const providers = providersByType[currentType] || [];

  const handleTypeChange = (v: string) => {
    setValue("type", v as ChannelFormData["type"]);
    const typeProviders = providersByType[v] || [];
    if (typeProviders.length === 1) {
      setValue("provider", typeProviders[0].value);
    }
  };

  const handle = async (data: ChannelFormData) => {
    await onSubmit(data);
    reset();
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle>{channel ? "Editar canal" : "Novo canal"}</SheetTitle>
          <SheetDescription>
            {channel
              ? "Altere os dados do canal abaixo."
              : "Preencha os dados para criar um novo canal."}
          </SheetDescription>
        </SheetHeader>
        <form
          onSubmit={handleSubmit(handle)}
          className="flex flex-1 flex-col gap-4 px-4"
        >
          <div className="space-y-2">
            <Label htmlFor="name">Nome *</Label>
            <Input
              id="name"
              {...register("name")}
              placeholder="Nome do canal"
            />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="identifier">Identificador</Label>
            <Input
              id="identifier"
              {...register("identifier")}
              placeholder="Detectado automaticamente ao conectar WhatsApp"
            />
            <p className="text-[11px] text-muted-foreground">
              Opcional — sera preenchido automaticamente ao conectar o WhatsApp.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Tipo *</Label>
            <Select
              value={currentType}
              onValueChange={handleTypeChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {channelTypeOptions.map((opt) => (
                  <SelectItem
                    key={opt.value}
                    value={opt.value}
                    disabled={!opt.available}
                  >
                    <div className="flex items-center gap-2">
                      <opt.icon className={`h-4 w-4 ${opt.color}`} />
                      {opt.label}
                      {!opt.available && (
                        <span className="ml-1 text-[10px] font-medium text-muted-foreground">
                          (em breve)
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.type && (
              <p className="text-xs text-destructive">{errors.type.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Provider *</Label>
            {providers.length > 0 ? (
              <Select
                value={currentProvider}
                onValueChange={(v) => setValue("provider", v)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value="—"
                disabled
                className="text-muted-foreground"
              />
            )}
            {errors.provider && (
              <p className="text-xs text-destructive">
                {errors.provider.message}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={currentStatus}
              onValueChange={(v) => setValue("status", v)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativo</SelectItem>
                <SelectItem value="inactive">Inativo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <SheetFooter className="mt-auto">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Salvando..." : "Salvar"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
